<template>
    <div ref="taginput">
        <AutoComplete
            v-bind="$attrs"
            ref="autocomplete"
            :aria-describedby="maxtags ? counterId : undefined"
            :aria-invalid="isLimitReached"
            :aria-errormessage="isLimitReached ? counterId : undefined"
            :model-value="modelValue"
            :suggestions="data"
            :placeholder="computedPlaceholder"
            :typeahead="autocomplete"
            :option-label="field"
            :complete-on-focus="openOnFocus"
            :loading="loading"
            :disabled="disabled"
            :append-to="appendTo"
            :input-class="{ 'tw:hidden': isLimitReached }"
            :pt="{
                overlay: {
                    class: 'tw:mt-1 tw:!py-2 tw:!px-3'
                },

                optionGroup: {
                    class: 'tw:!py-2 tw:!px-3 tw:!text-sm tw:!font-bold'
                },

                option: {
                    class: 'tw:!py-2 tw:!px-3 tw:!text-sm'
                },

                emptyMessage: {
                    class: 'tw:!text-sm tw:!p-2'
                }
            }"
            multiple
            fluid
            @update:model-value="addNewTag"
            @complete="handleComplete"
            @option-select="handleItemSelect"
            @before-hide="handleBeforeHide"
            @focus="handleFocus"
            @keydown="handleKeyDown"
        >
            <template v-if="$slots.default" #option="slotProps">
                <slot v-bind="{ option: slotProps.option }" />
            </template>

            <template v-if="$slots.empty" #empty>
                <slot name="empty" />
            </template>
        </AutoComplete>
        <p
            v-show="maxtags"
            :id="counterId"
            class="tw:text-right tw:text-base tw:mt-2! tw:text-surface-700 dark:tw:text-surface-300"

            aria-live="polite"
            aria-atomic="true"
        >
            <span class="tw:sr-only">
                {{ tagAmount }} out of {{ maxtags }} tags selected.
                {{ isLimitReached ? 'Limit reached. Remove a tag to add more.' : '' }}
            </span>

            <span aria-hidden="true">
                {{ tagAmount }} / {{ maxtags }}
            </span>
        </p>
    </div>
</template>

<script>
/**
 * ETaginput - PrimeVue implementation of a tag input component
 *
 * USAGE EXAMPLES:
 *
 *   // Basic autocomplete with typing event
 *   <e-taginput
 *     v-model="tags"
 *     :data="filteredTags"
 *     autocomplete
 *     placeholder="Add a tag"
 *     @typing="handleTyping">
 *   </e-taginput>
 *
 *   // With custom field and options
 *   <e-taginput
 *     v-model="selectedTags"
 *     :data="availableTags"
 *     field="name"
 *     :allow-new="false"
 *     icon="tag"
 *     placeholder="Select tags">
 *   </e-taginput>
 *
 *   // With max tags limit
 *   <e-taginput
 *     v-model="tags"
 *     :maxtags="5"
 *     placeholder="Up to 5 tags">
 *   </e-taginput>
 *
 * REF ACCESS:
 *   Components can access this component's root element via ref:
 *
 *   // Option 1: Use the helper method (recommended)
 *   const element = this.$refs.myTaginput.getRootElement();
 *
 *   // Option 2: Use fallback pattern
 *   const element = this.$refs.myTaginput.$el || this.$refs.myTaginput;
 */
export default {
    name: 'ETaginput',
    inheritAttrs: false,

    props: {
        modelValue: {
            type: Array,
            default: () => [],
        },
        data: {
            type: Array,
            default: () => [],
        },

        placeholder: {
            type: String,
            default: '',
        },
        field: {
            type: String,
            default: '',
        },
        autocomplete: {
            type: Boolean,
            default: false,
        },
        allowDuplicates: {
            type: Boolean,
            default: false,
        },
        openOnFocus: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        appendToBody: {
            type: Boolean,
            default: false,
        },
        allowNew: {
            type: Boolean,
            default: false,
        },
        maxtags: {
            type: Number,
            default: undefined,
        },
        icon: {
            type: String,
            default: undefined,
        },
    },

    emits: ['update:modelValue', 'add', 'remove', 'typing'],

    data() {
        return {
            keepOpen: false,
            currentQuery: '',
            counterId: `tag-counter-${Math.random().toString(36).substring(2, 9)}`,
        };
    },

    computed: {
        appendTo() {
            return this.appendToBody ? 'body' : 'self';
        },

        tagAmount() {
            return this.modelValue.length;
        },

        isLimitReached() {
            return this.maxtags && this.modelValue.length >= this.maxtags;
        },

        computedPlaceholder() {
            if (this.isLimitReached) {
                return '';
            }

            return this.placeholder;
        },
    },

    methods: {
        getFieldValue(item, fieldPath) {
            if (!fieldPath) {
                return item;
            }

            const parts = fieldPath.split('.');
            return parts.reduce((value, part) => value?.[part], item);
        },

        removeDuplicates(items) {
            if (!this.field) {
                return [...new Set(items)];
            }

            return this.removeDuplicatesByField(items);
        },

        removeDuplicatesByField(items) {
            const seen = new Set();

            return items.reduce((uniqueItems, item) => {
                const fieldValue = this.getFieldValue(item, this.field);

                if (fieldValue === undefined) {
                    console.error(`ETaginput: Item is missing the specified field "${this.field}":`, item);
                    uniqueItems.push(item);
                    return uniqueItems;
                }

                if (!seen.has(fieldValue)) {
                    seen.add(fieldValue);
                    uniqueItems.push(item);
                }

                return uniqueItems;
            }, []);
        },

        emitChangeEvents(newTags, previousTags) {
            const addedCount = newTags.length - previousTags.length;

            const isSameItem = (a, b) => {
                if (!this.field) {
                    return a === b;
                }
                return this.getFieldValue(a, this.field) === this.getFieldValue(b, this.field);
            };

            if (addedCount > 0) {
                const addedItem = newTags.find(item => !previousTags.some(prev => isSameItem(prev, item)));
                this.$emit('add', addedItem);
            } else if (addedCount < 0) {
                const removedItem = previousTags.find(item => !newTags.some(curr => isSameItem(curr, item)));
                this.$emit('remove', removedItem);
            }
        },

        addNewTag(newTags) {
            const previousTags = this.modelValue;
            const isAddingTag = newTags.length > previousTags.length;

            if (this.maxtags && isAddingTag && this.isLimitReached) {
                this.$emit('update:modelValue', [...previousTags]);
                return;
            }

            const processedTags = this.allowDuplicates ? newTags : this.removeDuplicates(newTags);

            // Update v-model before add/remove so listeners (e.g. TagSwitcher) read the new selection.
            this.$emit('update:modelValue', processedTags);
            this.emitChangeEvents(processedTags, previousTags);
        },

        handleComplete(event) {
            if (this.isLimitReached) {
                this.currentQuery = '';
                this.$emit('typing', '');
                return;
            }

            this.currentQuery = event.query || '';
            this.$emit('typing', event.query);
        },

        openDropdown() {
            if (this.isLimitReached) {
                return;
            }

            if (this.$refs.autocomplete && this.autocomplete) {
                this.$refs.autocomplete.search(null, this.currentQuery || '');
            }
        },

        handleFocus(event) {
            if (!this.openOnFocus) {
                return;
            }
            this.currentQuery = event?.target?.value || '';
            this.openDropdown();
        },

        handleItemSelect() {
            this.keepOpen = true;
            this.currentQuery = '';
        },

        handleBeforeHide() {
            if (this.keepOpen) {
                this.$nextTick(() => {
                    this.openDropdown();
                    this.keepOpen = false;
                });
            }
        },

        handleKeyDown(event) {
            if (this.autocomplete && this.allowNew && event.key === 'Enter') {
                if (this.isLimitReached) {
                    event.preventDefault();
                    return;
                }

                const text = event.target.value;

                if (!text || text.trim() === '') {
                    return;
                }

                event.preventDefault();

                const newItem = this.createItemFromInput(text.trim());
                this.addNewTag([...this.modelValue, newItem]);

                this.keepOpen = true;
                event.target.value = '';
                this.currentQuery = '';
                this.$emit('typing', '');
            }
        },

        getRootElement() {
            return this.$refs.taginput?.$el || this.$refs.taginput;
        },

        createItemFromInput(text) {
            if (!this.field) return text;

            if (!this.field.includes('.')) {
                return { [this.field]: text };
            }

            const result = {};
            const parts = this.field.split('.');

            const deepestObject = parts.slice(0, -1).reduce((current, part) => {
                current[part] = {};
                return current[part];
            }, result);

            deepestObject[parts[parts.length - 1]] = text;
            return result;
        },
    },
};
</script>

<style lang="scss" scoped>
@import 'resources/scss/toolbox.scss';

:deep(.p-autocomplete-input-multiple) {
    background-color: var(--p-inputtext-background);
    border: 1px solid var(--p-inputtext-border-color);
    border-radius: var(--p-inputtext-border-radius);
    color: var(--p-inputtext-color);
    box-shadow: var(--p-inputtext-shadow);

    padding: $control-padding-vertical $control-padding-horizontal !important;
    margin: 0 !important;

    min-height: $input-height;
    line-height: $control-line-height;
    font-size: $size-normal;
    transition: border-color 0.2s ease;
    max-width: 100%;
    width: 100%;
}

/* Ensure individual input chips inside also have proper spacing */
:deep(.p-autocomplete-input-multiple .p-autocomplete-input-chip) {
    margin: 0.125rem !important;
    padding: 0.25rem 0.5rem !important;
}

:deep(.p-autocomplete-input-multiple:hover) {
    border-color: var(--p-inputtext-hover-border-color);
}

:deep(.p-autocomplete-input-multiple:focus),
:deep(.p-autocomplete-input-multiple:focus-within) {
    border-color: var(--p-inputtext-focus-border-color);
    box-shadow: var(--p-inputtext-focus-ring-shadow);
    outline: none;
}

:deep(.p-autocomplete-input-multiple.p-disabled) {
    background-color: var(--p-inputtext-disabled-background);
    border-color: var(--p-inputtext-disabled-background);
    box-shadow: none;
    color: var(--p-inputtext-disabled-color);
    cursor: not-allowed;
    opacity: 0.6;
}
</style>
